/**
 * Hook to auto-share notes with organization managers and admins.
 *
 * Only MEMBER-role users auto-share — managers and admins must
 * share manually via the share sheet. This prevents managers from
 * flooding other managers with auto-shared notes on every creation.
 *
 * Encapsulates the org user query, manager ID filtering, and share
 * mutation so both NotesFormContainer and NotesSectionContainer
 * can share notes without duplicating logic.
 */

import { useCallback, useMemo } from "react";

import { useCurrentUser } from "@/features/auth";
import {
  RoleName,
  useListKanbanOrganizationUsersQuery,
  useNotesShareNoteMutation,
} from "@/generated/graphql";

/**
 * Request enough org users to include managers/admins, which may
 * appear beyond the default page. The backend returns users in
 * arbitrary order, so a small first-page (e.g. 50) may contain
 * only MEMBER roles and miss all managers entirely (SE-3990).
 */
const ORG_USERS_PAGE_SIZE = 500;

/**
 * Provides a callback to auto-share a note with all managers and admins
 * in the current user's organization (excluding the current user).
 * Returns a no-op when the current user is not a MEMBER, since only
 * members auto-share — managers/admins share manually via the share sheet.
 * @returns shareNoteWithManagers - call with a noteId after creation
 */
const useAutoShareWithManagers = () => {
  const currentUser = useCurrentUser();
  const [shareNote] = useNotesShareNoteMutation();

  /** Only members auto-share — managers/admins share manually via share sheet */
  const isMember = currentUser?.role?.name === RoleName.Member;

  const { data: orgUsersData } = useListKanbanOrganizationUsersQuery({
    variables: { first: ORG_USERS_PAGE_SIZE },
    skip: !isMember,
  });

  /** Manager and admin user IDs to auto-share new notes with */
  const managerUserIds = useMemo(
    () =>
      isMember
        ? (orgUsersData?.listMyOrganizationsOrganizationUsers?.edges
            ?.map(edge => edge.node)
            .filter(
              orgUser =>
                orgUser.role?.name === RoleName.Manager ||
                orgUser.role?.name === RoleName.Admin
            )
            .filter(orgUser => orgUser.user?.id !== currentUser?.id)
            .map(orgUser => orgUser.user?.id)
            .filter((id): id is string => !!id) ?? [])
        : [],
    [orgUsersData, currentUser?.id, isMember]
  );

  /**
   * Share a note with all managers and admins in the organization.
   * Awaits all share mutations so the backend creates share records
   * and sends notifications before callers proceed (SE-3990).
   * @param noteId - The ID of the note to share
   * @param label - Logging label to identify the caller
   */
  const shareNoteWithManagers = useCallback(
    async (noteId: string, label: string) => {
      if (managerUserIds.length === 0) return;

      /** Allow backend to fully process note creation before sharing (SE-3990) */
      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        await Promise.all(
          managerUserIds.map(targetUserId =>
            shareNote({
              variables: { input: { noteId, targetUserId } },
            })
          )
        );
      } catch (error) {
        console.warn(`[${label}] Auto-share failed for note`, noteId, error);
      }
    },
    [managerUserIds, shareNote]
  );

  return { shareNoteWithManagers, managerUserIds };
};

export default useAutoShareWithManagers;
